<template>
  <!-- Pins the search + switchers row (or the bulk-actions bar that replaces
       it) below the app bar while the page scrolls, so filters and sorting
       stay reachable without scrolling back to the top. When StickyBrandingBar
       is on the page it pins below that instead, so the two stack.
       Carries no gutter of its own: the slotted content is already a `v-col`,
       so the original spacing is preserved and the opaque background covers
       the whole column box. -->
  <v-col cols="12" class="StickyFilterBar pa-0" :style="offsetStyle">
    <slot />
  </v-col>
</template>

<script setup>
import { useStickyTop, STICKY_BRANDING_BAR_CLASS } from '@/composables/useStickyTop'

// The branding band pins directly above this bar on mobile and in the extension
// popup. Measured rather than assumed: its height follows the viewport width.
const { offsetStyle } = useStickyTop(`.${STICKY_BRANDING_BAR_CLASS}`)
</script>

<style lang="scss" scoped>
// The desktop-only `<v-col><v-divider class="my-2" /></v-col>` that this bar's
// border replaces also carried the vertical rhythm below the bar:
// 12px col gutter + 8px divider margin + 1px rule + 8px + 12px.
// Reproduced as a margin so desktop spacing is unchanged -- margin sits outside
// the border box, so the opaque plane and the border itself do not move.
$replaced-divider-height: 41px;

.StickyFilterBar {
  @include sticky-page-bar;
  // Replaces the desktop-only <v-divider color="neutral50"> that used to sit
  // directly below the bar.
  border-bottom: 1px solid rgb(var(--v-theme-neutral50));

  // Matches the breakpoint the replaced divider was gated on (`mdAndUp`). The
  // extension popup reported as desktop but is never this wide, so it keeps the
  // tighter spacing it had before without needing its own branch.
  @include md-up {
    margin-bottom: $replaced-divider-height;
  }
}
</style>
